<template>
  <section class="passenger-picker">
    <ul>
      <li
        v-for="type in passengerTypes"
        :key="type.code"
        class="passenger-picker__picker body-large"
      >
        <div class="passenger-picker__picker-node">
          <span>{{ type.description }}</span>
          <span class="passenger-picker__actions">
            <button
              v-if="getPassengerCount(type) > 0"
              class="passenger-picker__minus-button passenger-picker__button button--secondary"
              @click="decrementPassenger(type)"
              :aria-label="
                $t('travelPlanner.passenger.removePassenger', {
                  type: type.description,
                })
              "
            >
              <svg>
                <use :xlink:href="`#${minus}`" />
              </svg>
            </button>
            <span
              v-if="getPassengerCount(type) > 0"
              class="passenger-picker__amount"
            >
              {{ getPassengerCount(type) }}
            </span>
            <button
              class="passenger-picker__plus-button passenger-picker__button button--secondary"
              @click="incrementPassenger(type)"
              :aria-label="
                $t('travelPlanner.passenger.addPassenger', {
                  type: type.description,
                })
              "
            >
              <svg>
                <use :xlink:href="`#${plus}`" />
              </svg>
            </button>
          </span>
        </div>
        <div
          class="passenger-picker__info body-large"
          v-if="getPassengerCount(type) > 0"
        >
          <p>{{ $t(`descriptions.passengerTypes.${type.code}`) }}</p>
          <span
            v-for="passenger in getPassengersByType(type)"
            :key="passenger.id"
            class="dropdown-container"
          >
            <Dropdown
              v-if="dropdownVisible[passenger.id]"
              :options="cardTypes"
              :placeholder="
                $t('travelPlanner.passenger.travelCard.placeholder')
              "
              icon="discount"
              @update:selected="updateDiscountCard(type, passenger.id, $event)"
            />
            <button
              v-if="dropdownVisible[passenger.id]"
              class="dropdown__delete-button"
              @click="removeDiscountCard(type, passenger.id)"
              type="button"
            >
              <svg class="dropdown__icon-delete">
                <use :xlink:href="`#${cancel}`" />
              </svg>
            </button>
          </span>
          <div class="passenger-picker__button-container">
            <button
              v-if="!allPassengersHaveDiscount(type)"
              class="passenger-picker__travel-card-button"
              @click="showDropdown(type)"
            >
              + {{ $t('travelPlanner.passenger.travelCard.addTravelCard') }}
            </button>
          </div>
        </div>
      </li>
    </ul>
  </section>
</template>

<script lang="ts">
  import { defineComponent, ref, onMounted, PropType, watch } from 'vue'
  import plus from '@/assets/icons/plus.svg'
  import minus from '@/assets/icons/minus.svg'
  import discount from '@/assets/icons/discount.svg'
  import cancel from '@/assets/icons/cancel.svg'
  import { useJourneyStore } from '@/stores/journeyStore'
  import {
    PassengerType,
    Passenger,
    DiscountCard,
  } from '@/types/passengerTypes'
  import {
    fetchPassengerTypes,
    fetchCardTypes,
  } from '@/services/journeyService'
  import Dropdown from '@/components/BaseComponents/Dropdown.vue'

  export default defineComponent({
    name: 'PassengerPicker',
    components: {
      Dropdown,
    },
    props: {
      initialPassengers: {
        type: Array as PropType<Passenger[]>,
        default: () => [],
      },
    },
    setup(props) {
      const journeyStore = useJourneyStore()
      const passengerTypes = ref<PassengerType[]>([])
      const passengers = ref<Passenger[]>([])
      const discountInputs = ref<{ [key: string]: string }>({})
      const cardTypes = ref<{ label: string; value: string }[]>([])
      const cardTypeData = ref<{ [key: string]: any }>({})
      const dropdownVisible = ref<{ [key: string]: boolean }>({})

      // Get the highest existing passenger number to ensure unique IDs
      const getNextPassengerId = () => {
        const existingIds = passengers.value.map((p) => {
          const match = p.id.match(/passenger_(\d+)/)
          return match ? parseInt(match[1]) : 0
        })
        const highestId = Math.max(0, ...existingIds)
        return `passenger_${highestId + 1}`
      }

      // Function to map passenger data
      const mapPassenger = (passenger: any) => ({
        id: passenger.id || getNextPassengerId(),
        type: passenger.type,
        description: passenger.description || passengerTypes.value.find(t => t.code === passenger.type)?.description || '',
        discount_cards: passenger.discount_cards || [],
        first_name: passenger.first_name || '',
        last_name: passenger.last_name || '',
        email: passenger.email || '',
        phone_number: passenger.phone_number || '',
        protect_privacy: passenger.protect_privacy || false
      })

      onMounted(async () => {
        try {
          const types = await fetchPassengerTypes()
          passengerTypes.value = types.sort((a, b) => a.sequence_number - b.sequence_number)

          if (props.initialPassengers && props.initialPassengers.length > 0) {
            passengers.value = props.initialPassengers.map(mapPassenger)
          } else {
            const adultType = passengerTypes.value.find(
              (type: PassengerType) => type.code === 'ADT'
            )
            if (adultType) {
              passengers.value = [{
                id: getNextPassengerId(),
                type: adultType.code,
                description: adultType.description,
                discount_cards: [],
              }]
            }
          }

          // Initialize dropdownVisible for each passenger
          passengers.value.forEach(passenger => {
            dropdownVisible.value[passenger.id] = false
          })

          // Update journey store with initial passengers
          if (passengers.value.length > 0) {
            journeyStore.setPassengers([...passengers.value])
          }
        } catch (error) {
          console.error('Error initializing PassengerPicker:', error)
        }
      })

      // Watch for changes in initialPassengers prop
      watch(() => props.initialPassengers, (newPassengers) => {
        if (newPassengers && newPassengers.length > 0 && 
            JSON.stringify(newPassengers) !== JSON.stringify(passengers.value)) {
          passengers.value = newPassengers.map(mapPassenger)
        }
      }, { deep: true })

      // Watch local passengers changes and update store
      watch(passengers, (newPassengers) => {
        if (JSON.stringify(newPassengers) !== JSON.stringify(journeyStore.passengers)) {
          journeyStore.setPassengers([...newPassengers])
        }
      }, { deep: true })

      const getPassengerCount = (type: PassengerType) => {
        return passengers.value.filter((p) => p.type === type.code).length
      }

      const getPassengersByType = (type: PassengerType) => {
        return passengers.value.filter((p) => p.type === type.code)
      }

      const incrementPassenger = (type: PassengerType) => {
        const newPassenger = {
          id: getNextPassengerId(),
          type: type.code,
          description: type.description,
          discount_cards: [],
        }
        passengers.value = [...passengers.value, newPassenger]
      }

      const decrementPassenger = (type: PassengerType) => {
        const index = passengers.value
          .map((p) => p.type)
          .lastIndexOf(type.code)
        if (index !== -1) {
          passengers.value = [
            ...passengers.value.slice(0, index),
            ...passengers.value.slice(index + 1),
          ]
        }
      }

      const allPassengersHaveDiscount = (type: PassengerType) => {
        return getPassengersByType(type).every(
          (passenger) => dropdownVisible.value[passenger.id]
        )
      }

      const updateDiscountCards = (
        type: PassengerType,
        passengerId: string
      ) => {
        const discountCode = discountInputs.value[passengerId]
        passengers.value.forEach((passenger) => {
          if (passenger.id === passengerId) {
            if (discountCode) {
              const cardData = cardTypeData.value[discountCode]
              if (cardData) {
                passenger.discount_cards = [
                  {
                    code: cardData.value,
                    description: cardData.label,
                    name: cardData.label,
                    requires_passenger_identifier:
                      cardData.requires_passenger_identifier || false,
                  },
                ]
              }
            } else {
              passenger.discount_cards = []
            }
          }
        })
        journeyStore.setPassengers(passengers.value)
      }

      const showDropdown = async (type: PassengerType) => {
        try {
          const response = await fetchCardTypes()
          cardTypes.value = response
          cardTypeData.value = response.reduce((acc: any, card: any) => {
            acc[card.value] = card
            return acc
          }, {})
          const passengersByType = getPassengersByType(type)
          for (const passenger of passengersByType) {
            if (!dropdownVisible.value[passenger.id]) {
              dropdownVisible.value[passenger.id] = true
              break
            }
          }
        } catch (error) {
          console.error('Error fetching card types:', error)
        }
      }

      const updateDiscountCard = (
        type: PassengerType,
        passengerId: string,
        selectedValue: string
      ) => {
        discountInputs.value[passengerId] = selectedValue
        updateDiscountCards(type, passengerId)
      }

      const removeDiscountCard = (type: PassengerType, passengerId: string) => {
        passengers.value.forEach((passenger) => {
          if (passenger.id === passengerId) {
            passenger.discount_cards = []
          }
        })
        dropdownVisible.value[passengerId] = false
        journeyStore.setPassengers(passengers.value)
      }

      return {
        passengerTypes,
        passengers,
        discountInputs,
        cardTypes,
        cardTypeData,
        dropdownVisible,
        plus,
        minus,
        cancel,
        getPassengerCount,
        getPassengersByType,
        incrementPassenger,
        decrementPassenger,
        allPassengersHaveDiscount,
        updateDiscountCards,
        showDropdown,
        updateDiscountCard,
        removeDiscountCard,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .passenger-picker {
    @include body-large;
    max-width: 606px;
    width: 100%;
    margin: 0 auto 96px;

    @media (max-width: 606px) {
      width: 100%;
    }

    h3 {
      text-align: left;
      color: $clr-primary-800;
    }
    &__actions {
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    svg {
      height: 12.8px;
      width: 12.8px;
    }
    &__picker {
      display: flex;
      flex-direction: column;
      background-color: $clr-white;
      color: $clr-primary-800;
      border-radius: $border-radius;
      padding: 1rem 1rem 0 1rem;
      margin-bottom: 0.75rem;
      box-shadow: 0px 0px 4px 1px #2a347a26;
    }

    &__picker-node {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding-bottom: 1rem;
      align-items: center;
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    &__button {
      border-radius: 50%;
      height: 32px;
      width: 32px;
      border: 1px solid $clr-primary-200;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 8px;
      padding: 0;
      cursor: pointer;

      svg {
        width: 24px;
        height: 24px;
      }
    }

    &__amount {
      min-width: 24px;
      text-align: center;
    }

    &__info {
      border-top: 1px solid $clr-primary-400;
      text-align: left;
      padding: 1rem 0;
      margin: 0;
    }

    &__button-container {
      display: flex;
      justify-content: center;
    }

    &__travel-card-button {
      @include body-large;
      border: none;
      background-color: transparent;
      color: $clr-primary-400;
      text-align: center;
      padding: 8px;
      cursor: pointer;
    }

    .dropdown-container {
      display: flex;
      align-items: center;
    }

    .dropdown__delete-button {
      background: none;
      border: none;
      cursor: pointer;
      padding: 0;
      margin: 0 0 rem(8) 8px;
    }

    .dropdown__icon-delete {
      width: 16px;
      height: 16px;
      fill: currentColor;
      color: $clr-primary-300;
    }
  }
</style>
